    import React, { useState, useEffect } from 'react';
    import { ChevronRight, ChevronDown, Link, Database, Calendar, RefreshCw, Search, Filter } from 'lucide-react';
    import { api } from '../../api/client';

    // Helper to capitalize names
    const formatName = (name) => {
        if (!name) return '';
        return name
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const AdminFeatureManager = () => {
    const [features, setFeatures] = useState([]);
    const [eventTypes, setEventTypes] = useState([]);
    const [tables, setTables] = useState([]);
    const [selectedFeature, setSelectedFeature] = useState(null);
    const [loading, setLoading] = useState(false);
    const [syncStatus, setSyncStatus] = useState(null);
    const [expandedFeatures, setExpandedFeatures] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categories, setCategories] = useState([]);

    // Fixed API base path
    const API_BASE = '/api/internal-sync';

    useEffect(() => {
        fetchSyncStatus();
        fetchFeatures();
        fetchEventTypes();
        fetchTables();
        fetchCategories();
    }, []);

    const fetchSyncStatus = async () => {
        try {
        const response = await api.get(`${API_BASE}/status`);
        setSyncStatus(response.data);
        } catch (error) {
        console.error('Failed to fetch sync status:', error);
        }
    };

    const fetchFeatures = async () => {
        try {
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        if (selectedCategory) params.append('category', selectedCategory);
        
        const response = await api.get(`${API_BASE}/features?${params}`);
        setFeatures(response.data.features || []);
        } catch (error) {
        console.error('Failed to fetch features:', error);
        setFeatures([]);
        }
    };

    const fetchEventTypes = async () => {
        try {
        const response = await api.get(`${API_BASE}/event-types`);
        setEventTypes(response.data.event_types || []);
        } catch (error) {
        console.error('Failed to fetch event types:', error);
        setEventTypes([]);
        }
    };

    const fetchTables = async () => {
        try {
        const response = await api.get(`${API_BASE}/tables`);
        setTables(response.data.tables || []);
        } catch (error) {
        console.error('Failed to fetch tables:', error);
        setTables([]);
        }
    };

    const fetchCategories = async () => {
        try {
        const response = await api.get(`${API_BASE}/categories`);
        setCategories(response.data.feature_categories || []);
        } catch (error) {
        console.error('Failed to fetch categories:', error);
        setCategories([]);
        }
    };

    const fetchFeatureDetail = async (featureId) => {
        try {
        const response = await api.get(`${API_BASE}/features/${featureId}`);
        setSelectedFeature(response.data);
        } catch (error) {
        console.error('Failed to fetch feature detail:', error);
        }
    };

    const triggerSync = async () => {
        setLoading(true);
        try {
        // Use the correct endpoint for triggering sync
        const response = await api.post(`${API_BASE}/load`);
        alert(response.data.message || 'Sync initiated successfully');
        
        // Poll for status updates
        setTimeout(() => {
            fetchSyncStatus();
            fetchFeatures();
            fetchEventTypes();
            fetchTables();
        }, 2000);
        } catch (error) {
        console.error('Failed to trigger sync:', error);
        alert(error.response?.data?.detail || 'Failed to trigger sync');
        } finally {
        setLoading(false);
        }
    };

    const toggleFeatureExpansion = (featureId) => {
        const newExpanded = new Set(expandedFeatures);
        if (newExpanded.has(featureId)) {
        newExpanded.delete(featureId);
        } else {
        newExpanded.add(featureId);
        fetchFeatureDetail(featureId);
        }
        setExpandedFeatures(newExpanded);
    };

    const saveFeatureLinks = async (featureId, selectedEvents, selectedTables) => {
        try {
        const response = await api.post(`${API_BASE}/features/${featureId}/link-events`, {
            feature_id: featureId,
            event_type_ids: selectedEvents,
            table_ids: selectedTables,
        });
        alert(`Successfully linked ${response.data.linked_events} events and ${response.data.linked_tables} tables`);
        fetchFeatures();
        } catch (error) {
        console.error('Failed to save links:', error);
        alert('Failed to save links');
        }
    };

    return (
        <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold">Internal System Sync Manager</h1>
            <button
                onClick={triggerSync}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Sync Now
            </button>
            </div>
            
            {syncStatus && (
            <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-50 p-4 rounded">
                <p className="text-sm text-gray-600">Last Sync</p>
                <p className="text-lg font-semibold">
                    {syncStatus.last_sync?.completed_at 
                    ? new Date(syncStatus.last_sync.completed_at).toLocaleString()
                    : 'Never'}
                </p>
                </div>
                <div className="bg-gray-50 p-4 rounded">
                <p className="text-sm text-gray-600">Features</p>
                <p className="text-lg font-semibold">{syncStatus.current_counts?.features || 0}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded">
                <p className="text-sm text-gray-600">Event Types</p>
                <p className="text-lg font-semibold">{syncStatus.current_counts?.event_types || 0}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded">
                <p className="text-sm text-gray-600">Tables</p>
                <p className="text-lg font-semibold">{syncStatus.current_counts?.tables || 0}</p>
                </div>
            </div>
            )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex gap-4">
            <div className="flex-1">
                <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                    type="text"
                    placeholder="Search features..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyUp={(e) => e.key === 'Enter' && fetchFeatures()}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                </div>
            </div>
            <select
                value={selectedCategory}
                onChange={(e) => {
                setSelectedCategory(e.target.value);
                fetchFeatures();
                }}
                className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                <option value="">All Categories</option>
                {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
                ))}
            </select>
            <button
                onClick={fetchFeatures}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
                <Filter className="w-4 h-4" />
            </button>
            </div>
        </div>

        {/* Features List */}
        <div className="bg-white rounded-lg shadow-sm">
            <div className="p-4 border-b">
            <h2 className="text-lg font-semibold">Features ({features.length})</h2>
            </div>
            <div className="divide-y">
            {features.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                No features synced yet. Click "Sync Now" to import features from the internal system.
                </div>
            ) : (
                features.map((feature) => (
                <div key={feature.id} className="p-4">
                    <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => toggleFeatureExpansion(feature.id)}
                    >
                    <div className="flex items-center gap-3">
                        {expandedFeatures.has(feature.id) ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                        <div>
                        <h3 className="font-medium text-gray-900">{feature.name}</h3>
                        <p className="text-sm text-gray-500">{feature.description}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-500">
                        {formatName(feature.category)} / {formatName(feature.module)}
                        </span>
                        <div className="flex gap-2">
                        <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                            <Calendar className="w-3 h-3" />
                            {feature.event_count || 0} events
                        </span>
                        <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                            <Database className="w-3 h-3" />
                            {feature.table_count || 0} tables
                        </span>
                        </div>
                    </div>
                    </div>

                    {expandedFeatures.has(feature.id) && selectedFeature?.id === feature.id && (
                    <FeatureLinkEditor
                        feature={selectedFeature}
                        allEventTypes={eventTypes}
                        allTables={tables}
                        onSave={saveFeatureLinks}
                    />
                    )}
                </div>
                ))
            )}
            </div>
        </div>
        </div>
    );
    };

    const FeatureLinkEditor = ({ feature, allEventTypes, allTables, onSave }) => {
    const [selectedEvents, setSelectedEvents] = useState(
        feature.event_types.map(e => e.id)
    );
    const [selectedTables, setSelectedTables] = useState(
        feature.tables.map(t => t.id)
    );

    const toggleEvent = (eventId) => {
        setSelectedEvents(prev => 
        prev.includes(eventId) 
            ? prev.filter(id => id !== eventId)
            : [...prev, eventId]
        );
    };

    const toggleTable = (tableId) => {
        setSelectedTables(prev => 
        prev.includes(tableId)
            ? prev.filter(id => id !== tableId)
            : [...prev, tableId]
        );
    };

    return (
        <div className="mt-4 ml-8 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-2 gap-6">
            {/* Event Types */}
            <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Event Types
            </h4>
            <div className="max-h-64 overflow-y-auto space-y-2">
                {allEventTypes.length === 0 ? (
                <p className="text-sm text-gray-500">No event types available</p>
                ) : (
                allEventTypes.map(event => (
                    <label key={event.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={selectedEvents.includes(event.id)}
                        onChange={() => toggleEvent(event.id)}
                        className="rounded text-blue-600"
                    />
                    <div>
                        <p className="text-sm font-medium">{event.name}</p>
                        <p className="text-xs text-gray-500">{event.trigger_type}</p>
                    </div>
                    </label>
                ))
                )}
            </div>
            </div>

            {/* Tables */}
            <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
                <Database className="w-4 h-4" />
                Database Tables
            </h4>
            <div className="max-h-64 overflow-y-auto space-y-2">
                {allTables.length === 0 ? (
                <p className="text-sm text-gray-500">No tables available</p>
                ) : (
                allTables.map(table => (
                    <label key={table.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={selectedTables.includes(table.id)}
                        onChange={() => toggleTable(table.id)}
                        className="rounded text-blue-600"
                    />
                    <div>
                        <p className="text-sm font-medium">{table.name}</p>
                        <p className="text-xs text-gray-500">{table.column_count || 0} columns</p>
                    </div>
                    </label>
                ))
                )}
            </div>
            </div>
        </div>

        <div className="mt-4 flex justify-end">
            <button
            onClick={() => onSave(feature.id, selectedEvents, selectedTables)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
            <Link className="w-4 h-4" />
            Save Links
            </button>
        </div>
        </div>
    );
    };

    export default AdminFeatureManager;